import { inject, Service } from '@angular/core';

import { EventSubType, FileSubType, ResourceType } from '@models';
import { isSet } from '@shared/utils';
import { DateFormatService } from './date-format.service';

export interface ValidationResult {
  isValid: boolean;
  invalidField?: string;
}

export interface ValidatableResource {
  name?: string;
  executors?: unknown[];
}

export interface DateTimeTmp {
  fromDate: Nullable<string>;
  fromDateTime: Nullable<{ hour: number; minute: number; second: number }>;
  toDate: Nullable<string>;
  toDateTime: Nullable<{ hour: number; minute: number; second: number }>;
}

export interface ResourceValidationContext {
  resource: ValidatableResource;
  resourceType: number;
  resourceSubType?: Nullable<number>;
  tmp?: DateTimeTmp;
  uploadQueue?: readonly unknown[];
  wizardStep: number;
  enableDetail?: boolean;
}

@Service()
export class ResourceValidationService {
  dateFormatService = inject(DateFormatService);

  validate = (context: ResourceValidationContext): ValidationResult => {
    if (context.wizardStep === 1) {
      return this.validateStep1(context);
    }

    if (context.wizardStep === 2) {
      return this.validateStep2(context);
    }

    return { isValid: true };
  };

  validateStep1 = (context: ResourceValidationContext): ValidationResult => {
    if (context.enableDetail) {
      return { isValid: false };
    }

    return { isValid: true };
  };

  validateStep2 = (context: ResourceValidationContext): ValidationResult => {
    const { resource, resourceType, resourceSubType } = context;

    switch (resourceType) {
      case ResourceType.ROOM:
        return this.validateRoom(resource);

      case ResourceType.FILE:
        if (resourceSubType === FileSubType.FILE) {
          return this.validateFileUpload(context);
        } else if (resourceSubType === FileSubType.FILE_NOTE) {
          return this.validateNote(resource);
        }

        break;

      case ResourceType.DISCUSSION:
        return this.validateDiscussion(resource);

      case ResourceType.EVENT:
        if (resourceSubType === EventSubType.EVENT) {
          return this.validateEvent(context);
        } else if (resourceSubType === EventSubType.MEETING) {
          return this.validateMeeting(context);
        }

        break;

      case ResourceType.AGENDA_POINT:
        return this.validateAgendaPoint(resource);

      case ResourceType.ACTIVITY:
        return this.validateTask(resource);
    }

    return this.validateNameRequired(resource);
  };

  validateRoom = (resource: ValidatableResource): ValidationResult => {
    if (!isSet(resource.name)) {
      return {
        isValid: false,
        invalidField: 'name',
      };
    }

    return { isValid: true };
  };

  validateFileUpload = (
    context: ResourceValidationContext
  ): ValidationResult => {
    const { uploadQueue } = context;

    if (!uploadQueue || uploadQueue.length === 0) {
      return {
        isValid: false,
        invalidField: 'files',
      };
    }

    return { isValid: true };
  };

  validateNote = (resource: ValidatableResource): ValidationResult => {
    if (!isSet(resource.name)) {
      return {
        isValid: false,
        invalidField: 'name',
      };
    }

    return { isValid: true };
  };

  validateDiscussion = (resource: ValidatableResource): ValidationResult => {
    if (!isSet(resource.name)) {
      return {
        isValid: false,
        invalidField: 'name',
      };
    }

    return { isValid: true };
  };

  validateEvent = (context: ResourceValidationContext): ValidationResult => {
    const { resource, tmp } = context;

    if (!isSet(resource.name)) {
      return {
        isValid: false,
        invalidField: 'name',
      };
    }

    if (!this.isTimeValid(tmp)) {
      return {
        isValid: false,
        invalidField: 'time',
      };
    }

    return { isValid: true };
  };

  validateMeeting = (context: ResourceValidationContext): ValidationResult => {
    const { resource, tmp } = context;

    if (!isSet(resource.name)) {
      return {
        isValid: false,
        invalidField: 'name',
      };
    }

    if (!this.isTimeValid(tmp)) {
      return {
        isValid: false,
        invalidField: 'time',
      };
    }

    return { isValid: true };
  };

  validateAgendaPoint = (resource: ValidatableResource): ValidationResult => {
    if (!isSet(resource.name)) {
      return {
        isValid: false,
        invalidField: 'name',
      };
    }

    return { isValid: true };
  };

  validateTask = (resource: ValidatableResource): ValidationResult => {
    if (!isSet(resource.name)) {
      return {
        isValid: false,
        invalidField: 'name',
      };
    }

    if (!resource.executors || resource.executors.length === 0) {
      return {
        isValid: false,
        invalidField: 'executors',
      };
    }

    return { isValid: true };
  };

  validateNameRequired = (resource: ValidatableResource): ValidationResult => {
    if (!isSet(resource.name)) {
      return {
        isValid: false,
        invalidField: 'name',
      };
    }

    return { isValid: true };
  };

  isTimeValid = (tmp?: ResourceValidationContext['tmp']): boolean => {
    if (!tmp) {
      return true;
    }

    if (tmp.fromDate && tmp.toDate && tmp.fromDateTime && tmp.toDateTime) {
      const from = this.dateFormatService.modelToDate(tmp.fromDate);
      const to = this.dateFormatService.modelToDate(tmp.toDate);

      if (!from || !to) {
        return true;
      }

      from.setHours(tmp.fromDateTime.hour);
      from.setMinutes(tmp.fromDateTime.minute);
      to.setHours(tmp.toDateTime.hour);
      to.setMinutes(tmp.toDateTime.minute);

      return from.getTime() <= to.getTime();
    }

    return true;
  };

  isPastEvent = (
    tmp?: Pick<DateTimeTmp, 'fromDate' | 'fromDateTime'>,
    today: Date = new Date()
  ): boolean => {
    if (!tmp?.fromDate) {
      return false;
    }

    const date = this.dateFormatService.modelToDate(tmp.fromDate);

    if (!date) {
      return false;
    }

    if (tmp.fromDateTime) {
      date.setHours(tmp.fromDateTime.hour, tmp.fromDateTime.minute, 0);
    }

    return date.getTime() <= today.getTime();
  };

  isFutureEvent = (
    tmp?: Pick<DateTimeTmp, 'toDate' | 'toDateTime'>,
    today: Date = new Date()
  ): boolean => {
    if (!tmp?.toDate) {
      return true;
    }

    const toDate = this.dateFormatService.modelToDate(tmp.toDate);

    if (!toDate) {
      return true;
    }

    if (tmp.toDateTime) {
      toDate.setHours(tmp.toDateTime.hour, tmp.toDateTime.minute, 0);
    }

    return toDate.getTime() > today.getTime();
  };
}
